import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createUserAsync, getUserByIdAsync, getUserRolesAsync, updateUserAsync } from '../../../features/users/usersActions';
import { clearUserStatus, selectCanManageUsers } from '../../../features/users/usersSlice';
import Toast from '../../../components/Toast';
import { fetchDepartments } from '../../../features/departments/departmentActions';
import { fetchJobTitles } from '../../../features/jobTitles/jobTitleActions';
const UserFormContext = createContext(null);
const emptyForm = { employeeId: '', fullName: '', email: '', mobileNumber: '', jobTitle: '',
  departmentName: '', departmentId: '', jobTitleId: '', branchName: '', roleId: '', status: 'ACTIVE', password: '' };
export function UserFormProvider({ id, children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canManage = useSelector(selectCanManageUsers);
  const { roles, rolesLoading, rolesError, saving, saveError } = useSelector(state => state.users);
  const profile = useSelector(state => id ? state.users.profiles[id] : null);
  const departmentState = useSelector(state => state.departments);
  const jobTitleState = useSelector(state => state.jobTitles);
  const [originalOrganisation, setOriginalOrganisation] = useState(null);
  const isEditing = Boolean(id);
  const [formData, setFormData] = useState(emptyForm);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ready, setReady] = useState(!isEditing);
  const [attempt, setAttempt] = useState(0);
  const [validationError, setValidationError] = useState('');
  const submitting = useRef(false);
  useEffect(() => { dispatch(clearUserStatus()); }, [dispatch, id]);
  useEffect(() => {
    if (!canManage) return;
    const requests = [dispatch(getUserRolesAsync()), dispatch(fetchDepartments()), dispatch(fetchJobTitles())];
    return () => requests.forEach(request => request.abort());
  }, [dispatch, canManage]);
  useEffect(() => {
    if (!isEditing || !canManage) return;
    let active = true;
    setReady(false);
    const request = dispatch(getUserByIdAsync(id));
    request.unwrap().then(payload => {
      if (!active) return;
      const user = payload.user || payload.data || payload;
      const values = { ...emptyForm };
      for (const field of Object.keys(values)) if (field !== 'password') values[field] = user[field] ?? values[field];
      values.roleId = String(values.roleId);
      values.departmentId = user.departmentId ? String(user.departmentId) : '';
      values.jobTitleId = user.jobTitleId ? String(user.jobTitleId) : '';
      setOriginalOrganisation({ departmentId: values.departmentId, jobTitleId: values.jobTitleId, departmentName: user.departmentName, jobTitle: user.jobTitle });
      setFormData(values); setConfirmPassword(''); setReady(true);
    }).catch(() => {});
    return () => { active = false; request.abort(); };
  }, [dispatch, id, isEditing, canManage, attempt]);
  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(previous => ({ ...previous, [name]: value, ...(name === 'departmentId' && value !== previous.departmentId ? { jobTitleId: '' } : {}) }));
    setValidationError('');
  };
  const fail = message => { setValidationError(message); Toast('error', message); };
  const handleSubmit = async event => {
    event.preventDefault();
    if (!canManage || !ready || saving || submitting.current || rolesLoading || rolesError || departmentState.loading || departmentState.error || jobTitleState.loading || jobTitleState.error) return;
    if (!['employeeId', 'fullName', 'email', 'branchName'].every(field => formData[field].trim())) return fail('Employee ID, full name, email and branch are required.');
    if (!roles.some(role => String(role.id) === String(formData.roleId))) return fail('Select a valid role.');
    if (!isEditing && !formData.password.trim()) return fail('Password is required.');
    if (formData.password !== confirmPassword) return fail('Password and confirm password do not match.');
    if (formData.password && !formData.password.trim()) return fail('Password cannot contain only spaces.');
    const data = { ...formData, roleId: Number(formData.roleId) };
    delete data.departmentName; delete data.jobTitle;
    data.departmentId = formData.departmentId ? Number(formData.departmentId) : null;
    data.jobTitleId = formData.jobTitleId ? Number(formData.jobTitleId) : null;
    if (isEditing && originalOrganisation && !originalOrganisation.departmentId && !originalOrganisation.jobTitleId && !formData.departmentId && !formData.jobTitleId) {
      delete data.departmentId; delete data.jobTitleId;
    }
    for (const field of ['employeeId', 'fullName', 'email', 'mobileNumber', 'branchName']) data[field] = data[field].trim();
    if (isEditing && !data.password) delete data.password;
    submitting.current = true; setValidationError('');
    try {
      const payload = await dispatch(isEditing ? updateUserAsync({ id, data }) : createUserAsync(data)).unwrap();
      const user = payload.user || payload.data || payload;
      Toast('success', isEditing ? 'User updated successfully.' : 'User created successfully.');
      navigate('/users/' + user.id);
    } catch (error) { Toast('error', String(error || 'Unable to save user.')); }
    finally { submitting.current = false; }
  };
  return <UserFormContext.Provider value={{ id, isEditing, canManage, formData, handleChange,
    departments: departmentState.items.filter(item => item.isActive || String(item.id) === originalOrganisation?.departmentId),
    jobTitles: jobTitleState.items.filter(item => String(item.departmentId) === formData.departmentId && ((item.isActive && item.department?.isActive) || String(item.id) === originalOrganisation?.jobTitleId)),
    organisationLoading: departmentState.loading || jobTitleState.loading,
    organisationError: departmentState.error || jobTitleState.error,
    reloadOrganisation: () => { dispatch(fetchDepartments()); dispatch(fetchJobTitles()); },
    legacyOrganisation: originalOrganisation && ((!originalOrganisation.departmentId && originalOrganisation.departmentName) || (!originalOrganisation.jobTitleId && originalOrganisation.jobTitle)),
    confirmPassword, setConfirmPassword, handleSubmit, roles, rolesLoading, rolesError,
    reloadRoles: () => dispatch(getUserRolesAsync()), saving, ready,
    loading: isEditing && (!ready && !profile?.error), loadError: profile?.error,
    reload: () => setAttempt(value => value + 1), error: validationError || saveError,
    submitDisabled: saving || !ready || rolesLoading || Boolean(rolesError) || !roles.length || departmentState.loading || jobTitleState.loading || Boolean(departmentState.error || jobTitleState.error) }}>{children}</UserFormContext.Provider>;
}
export function useUserForm() {
  const context = useContext(UserFormContext);
  if (!context) throw new Error('useUserForm requires UserFormProvider.');
  return context;
}
